import { API_URL } from "@/constants/api";
import { MergeSpotRequest, SpotAddRequest, SpotRequest } from "@/common/model/SpotRequest";
import { findProtectedSpot, getProtectedSpotWarning, isProtectedSpotError } from "@/constants/spots";

export interface SpotRequestResult {
  success: boolean;
  message?: string;
  isProtectedSpotError?: boolean;
}

export class SpotRequestService {
  private static baseUrl = API_URL;

  /**
   * Get pending merge requests (admin only)
   */
  static async getMergeRequests(accessToken: string): Promise<MergeSpotRequest[]> {
    try {
      const response = await fetch(
        `${this.baseUrl}/mergespotrequest/list/${accessToken}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );
      if (!response.ok) return [];
      return await response.json();
    } catch (error) {
      console.error("Error fetching merge requests:", error);
      return [];
    }
  }

  /**
   * Get pending spot add/rename requests (admin only)
   */
  static async getSpotAddRequests(accessToken: string): Promise<SpotAddRequest[]> {
    try {
      const response = await fetch(
        `${this.baseUrl}/spots/listrequests?accesstoken=${accessToken}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );
      if (!response.ok) return [];
      const data = await response.json();
      // Transform to our format
      // Note: Backend returns 'name' as the new name (for rename) or spot name (for add)
      // and 'oldname' as the previous name (only for rename requests)
      return data.map((item: any) => {
        const isChangeRequest = !!item.spotidtorename;
        const isRename = isChangeRequest && !!item.oldname;
        const oldLat = item.oldlatitude ?? null;
        const oldLon = item.oldlongitude ?? null;
        const newLat = item.latitude || item.lat;
        const newLon = item.longitude || item.lon;
        // Location changed if old coords exist and differ from new coords
        const locationChanged = oldLat != null && oldLon != null &&
          (Math.abs(newLat - oldLat) > 0.0001 || Math.abs(newLon - oldLon) > 0.0001);

        let type: 'ADD' | 'RENAME' | 'EDIT';
        if (!isChangeRequest) {
          type = 'ADD';
        } else if (locationChanged) {
          type = 'EDIT'; // Location changed (may also have name change)
        } else {
          type = 'RENAME'; // Name-only change
        }

        return {
          id: item.id,
          type,
          spotname: item.spotname || item.name,
          oldname: item.oldname,
          newname: isChangeRequest ? item.name : undefined,
          latitude: newLat,
          longitude: newLon,
          oldlatitude: oldLat,
          oldlongitude: oldLon,
          spotidtorename: item.spotidtorename ?? null,
          userid: item.userid,
          username: item.username,
          countryName: item.countryName,
          alpha2: item.alpha2,
          createdAt: item.createdAt,
          comment: item.comment,
        };
      });
    } catch (error) {
      console.error("Error fetching spot add requests:", error);
      return [];
    }
  }

  /**
   * Get all pending requests (merge + add/rename) combined
   */
  static async getAllPendingRequests(accessToken: string): Promise<SpotRequest[]> {
    try {
      const [mergeRequests, addRequests] = await Promise.all([
        this.getMergeRequests(accessToken),
        this.getSpotAddRequests(accessToken),
      ]);

      const allRequests: SpotRequest[] = [];

      // Add merge requests
      mergeRequests.forEach((req) => {
        allRequests.push({
          type: 'merge',
          id: req.id,
          data: req,
        });
      });

      // Add add/rename/edit requests
      addRequests.forEach((req) => {
        let type: 'add' | 'rename' | 'edit';
        if (req.type === 'EDIT') {
          type = 'edit';
        } else if (req.type === 'RENAME') {
          type = 'rename';
        } else {
          type = 'add';
        }
        allRequests.push({
          type,
          id: req.id,
          data: req,
        });
      });

      return allRequests;
    } catch (error) {
      console.error("Error fetching all pending requests:", error);
      return [];
    }
  }

  /**
   * Approve and execute spot merge (admin only)
   */
  static async approveMergeRequest(
    accessToken: string,
    targetSpotId: number,
    sourceSpotIds: number[],
    mergeRequestId?: number
  ): Promise<SpotRequestResult> {
    // Pre-flight check for protected spots
    const protectedSpotId = findProtectedSpot(sourceSpotIds);
    if (protectedSpotId !== null) {
      return {
        success: false,
        message: getProtectedSpotWarning(protectedSpotId),
        isProtectedSpotError: true,
      };
    }

    try {
      const params = new URLSearchParams({
        targetspotid: targetSpotId.toString(),
        spotstomergeids: sourceSpotIds.join(","),
      });
      if (mergeRequestId) {
        params.append("mergerequestid", mergeRequestId.toString());
      }

      const response = await fetch(
        `${this.baseUrl}/spots/mergespots/${accessToken}?${params.toString()}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        }
      );
      if (!response.ok) {
        // Try to parse as JSON for structured error
        try {
          const errorData = await response.json();
          if (isProtectedSpotError(errorData)) {
            return {
              success: false,
              message: errorData.message,
              isProtectedSpotError: true,
            };
          }
          return { success: false, message: errorData.message || "Failed to merge spots" };
        } catch {
          const errorText = await response.text();
          return { success: false, message: errorText || "Failed to merge spots" };
        }
      }
      return { success: true };
    } catch (error) {
      console.error("Error merging spots:", error);
      return { success: false, message: "Network error" };
    }
  }

  /**
   * Decline/delete a merge request (admin only)
   */
  static async declineMergeRequest(
    accessToken: string,
    requestId: number,
    reason?: string
  ): Promise<{ success: boolean; message?: string }> {
    try {
      const params = new URLSearchParams();
      if (reason) {
        params.append("reason", reason);
      }
      const queryString = params.toString();
      const url = `${this.baseUrl}/mergespotrequest/deleterequest/${accessToken}/${requestId}${queryString ? `?${queryString}` : ""}`;

      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) {
        const errorText = await response.text();
        return { success: false, message: errorText || "Failed to decline request" };
      }
      return { success: true };
    } catch (error) {
      console.error("Error declining merge request:", error);
      return { success: false, message: "Network error" };
    }
  }

  /**
   * Approve spot rename request (admin only)
   */
  static async approveRenameRequest(
    accessToken: string,
    requestId: number
  ): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await fetch(
        `${this.baseUrl}/spots/acceptrename/${requestId}/?accesstoken=${accessToken}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );
      if (!response.ok) {
        const errorText = await response.text();
        return { success: false, message: errorText || "Failed to approve rename request" };
      }
      return { success: true };
    } catch (error) {
      console.error("Error approving rename request:", error);
      return { success: false, message: "Network error" };
    }
  }

  /**
   * Approve spot add request by creating the spot (admin only)
   * Backend creates the spot and deletes the request in one operation
   */
  static async approveSpotAddRequest(
    accessToken: string,
    requestId: number,
    spotData: { name: string; latitude: number; longitude: number }
  ): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await fetch(
        `${this.baseUrl}/spots?accesstoken=${accessToken}&srid=${requestId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: spotData.name,
            latitude: spotData.latitude,
            longitude: spotData.longitude,
          }),
        }
      );
      if (!response.ok) {
        const errorText = await response.text();
        return { success: false, message: errorText || "Failed to create spot" };
      }
      return { success: true };
    } catch (error) {
      console.error("Error approving spot add request:", error);
      return { success: false, message: "Network error" };
    }
  }

  /**
   * Approve spot add/rename request (admin only)
   * Routes to appropriate method based on request type
   */
  static async approveAddRequest(
    accessToken: string,
    requestId: number,
    isRename: boolean = false,
    spotData?: { name: string; latitude: number; longitude: number }
  ): Promise<{ success: boolean; message?: string }> {
    // For rename requests, use the dedicated rename endpoint
    if (isRename) {
      return this.approveRenameRequest(accessToken, requestId);
    }

    // For new spot ADD requests, create the spot via POST /spots?srid=requestId
    if (!spotData) {
      return { success: false, message: "Spot data required for ADD requests" };
    }
    return this.approveSpotAddRequest(accessToken, requestId, spotData);
  }

  /**
   * Decline spot add/rename request (admin only)
   */
  static async declineAddRequest(
    accessToken: string,
    requestId: number,
    reason?: string
  ): Promise<{ success: boolean; message?: string }> {
    try {
      const params = new URLSearchParams({
        accesstoken: accessToken,
        silence: "false",
      });
      if (reason) {
        params.append("reason", reason);
      }

      const response = await fetch(
        `${this.baseUrl}/spots/deleterequests/${requestId}?${params.toString()}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );
      if (!response.ok) {
        const errorText = await response.text();
        return { success: false, message: errorText || "Failed to decline request" };
      }
      return { success: true };
    } catch (error) {
      console.error("Error declining add request:", error);
      return { success: false, message: "Network error" };
    }
  }

  /**
   * Get count of pending requests (for badge)
   */
  static async getPendingRequestCount(accessToken: string): Promise<number> {
    try {
      const [mergeRequests, addRequests] = await Promise.all([
        this.getMergeRequests(accessToken),
        this.getSpotAddRequests(accessToken),
      ]);
      return mergeRequests.length + addRequests.length;
    } catch (error) {
      console.error("Error fetching request count:", error);
      return 0;
    }
  }
}
